import { useEffect, useRef } from "react";
import { auth } from "../firebase";
import { notifyWebAppErrorToSlackServer } from "../services/functions";

const ERROR_SELECTOR = ".error";
const MESSAGE_MAX_LEN = 400;
const CONTEXT_MAX_LEN = 240;
const DEDUPE_TTL_MS = 10_000;

function normalize(value: unknown, maxLen: number, fallback: string): string {
  if (typeof value !== "string") return fallback;
  const cleaned = value.replace(/\s+/g, " ").trim();
  if (!cleaned) return fallback;
  return cleaned.slice(0, maxLen);
}

function getErrorText(node: Element): string {
  return normalize(node.textContent, MESSAGE_MAX_LEN, "unknown error");
}

function getContext(node: Element): string {
  const section = node.closest("section,form,article,main,header,footer,div");
  const titleNode = section?.querySelector("h1, h2, h3, [aria-label], label");
  const label =
    titleNode instanceof HTMLElement ?
      titleNode.innerText || titleNode.textContent :
      titleNode?.getAttribute("aria-label");
  return normalize(label, CONTEXT_MAX_LEN, "ui");
}

function eventKey(route: string, message: string, context: string): string {
  return `${route}::${context}::${message}`;
}

export default function ErrorSlackNotifier() {
  const recentEventsRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    const flushOldKeys = (now: number) => {
      const cache = recentEventsRef.current;
      for (const [key, timestamp] of cache.entries()) {
        if (now - timestamp > DEDUPE_TTL_MS) cache.delete(key);
      }
    };

    const reportElement = (el: Element) => {
      const message = getErrorText(el);
      const route = window.location.pathname || "/";
      const context = getContext(el);
      const key = eventKey(route, message, context);
      const now = Date.now();
      flushOldKeys(now);
      if (recentEventsRef.current.has(key)) return;
      recentEventsRef.current.set(key, now);

      void notifyWebAppErrorToSlackServer({
        errorMessage: message,
        pagePath: route,
        pageUrl: window.location.href || route,
        userAgent: navigator.userAgent || "unknown_user_agent",
        userId: auth.currentUser?.uid || "anonymous",
      }).catch((error) => {
        if (process.env.NODE_ENV !== "production") {
          console.warn("[slack] frontend error notification failed", error);
        }
      });
    };

    const existingErrors = document.querySelectorAll(ERROR_SELECTOR);
    existingErrors.forEach((node) => reportElement(node));

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches(ERROR_SELECTOR)) {
            reportElement(node);
            return;
          }
          const nestedErrors = node.querySelectorAll(ERROR_SELECTOR);
          nestedErrors.forEach((errorNode) => reportElement(errorNode));
        });
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  return null;
}
